// src/components/module/cotizaciones/documentos/CotizacionPDF.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import { formatMoney } from '../../../../utils/format';

interface ConfiguracionEmpresa {
  nombre: string;
  nit: string;
  direccion: string;
  telefono: string;
  email: string;
  sitioWeb?: string;
}

interface CotizacionPDFProps {
  cotizacion: {
    codigoCotizacion: string;
    fecha: string;
    cliente: {
      nombre: string;
      identificacion: string;
      direccion: string;
      telefono: string;
    };
    articulos: Array<{
      EAN: string;
      Nombre: string;
      Cantidad: number;
      PrecioVenta: number;
      Descuento: number;
      Impuesto: Array<{
        Valor: number;
      }>;
    }>;
    subtotal: number;
    descuentoTotal: number;
    impuestos: Array<{
      nombre: string;
      valor: number;
    }>;
    totalAPagar: number;
    observaciones?: string;
  };
  configuracionEmpresa: ConfiguracionEmpresa;
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  empresaInfo: {
    flex: 1,
  },
  cotizacionInfo: {
    width: 200,
    textAlign: 'right',
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  text: {
    marginBottom: 3,
  },
  clienteInfo: {
    marginBottom: 20,
  },
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingVertical: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#999',
    paddingVertical: 3,
  },
  colCodigo: {
    width: '15%',
  },
  colDescripcion: {
    width: '30%',
  },
  colCantidad: {
    width: '10%',
    textAlign: 'right',
  },
  colPrecio: {
    width: '15%',
    textAlign: 'right',
  },
  colDescuento: {
    width: '15%',
    textAlign: 'right',
  },
  colTotal: {
    width: '15%',
    textAlign: 'right',
  },
  totales: {
    width: '30%',
    alignSelf: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  bold: {
    fontWeight: 'bold',
  },
  observaciones: {
    marginTop: 20,
    padding: 10,
    backgroundColor: '#f8f8f8',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#999',
  },
});

export const CotizacionPDF: React.FC<CotizacionPDFProps> = ({ cotizacion, configuracionEmpresa }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Encabezado */}
      <View style={styles.header}>
        <View style={styles.empresaInfo}>
          <Text style={styles.title}>{configuracionEmpresa.nombre}</Text>
          <Text style={styles.text}>NIT: {configuracionEmpresa.nit}</Text>
          <Text style={styles.text}>{configuracionEmpresa.direccion}</Text>
          <Text style={styles.text}>Tel: {configuracionEmpresa.telefono}</Text>
          <Text style={styles.text}>{configuracionEmpresa.email}</Text>
          {configuracionEmpresa.sitioWeb && (
            <Text style={styles.text}>{configuracionEmpresa.sitioWeb}</Text>
          )}
        </View>
        <View style={styles.cotizacionInfo}>
          <Text style={styles.title}>COTIZACIÓN</Text>
          <Text style={styles.text}>N°: {cotizacion.codigoCotizacion}</Text>
          <Text style={styles.text}>Fecha: {cotizacion.fecha}</Text>
        </View>
      </View>

      {/* Información del Cliente */}
      <View style={styles.clienteInfo}>
        <Text style={styles.subtitle}>CLIENTE</Text>
        <Text style={styles.text}>Nombre: {cotizacion.cliente.nombre}</Text>
        <Text style={styles.text}>Identificación: {cotizacion.cliente.identificacion}</Text>
        <Text style={styles.text}>Dirección: {cotizacion.cliente.direccion}</Text>
        <Text style={styles.text}>Teléfono: {cotizacion.cliente.telefono}</Text>
      </View>

      {/* Tabla de Artículos */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.colCodigo}>Código</Text>
          <Text style={styles.colDescripcion}>Descripción</Text>
          <Text style={styles.colCantidad}>Cant.</Text>
          <Text style={styles.colPrecio}>Precio Unit.</Text>
          <Text style={styles.colDescuento}>Desc.</Text>
          <Text style={styles.colTotal}>Total</Text>
        </View>
        {cotizacion.articulos.map((articulo, index) => {
          const subtotal = articulo.PrecioVenta * articulo.Cantidad;
          const descuento = subtotal * (articulo.Descuento / 100);
          const total = subtotal - descuento;

          return (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.colCodigo}>{articulo.EAN}</Text>
              <Text style={styles.colDescripcion}>{articulo.Nombre}</Text>
              <Text style={styles.colCantidad}>{articulo.Cantidad}</Text>
              <Text style={styles.colPrecio}>{formatMoney(articulo.PrecioVenta)}</Text>
              <Text style={styles.colDescuento}>
                {articulo.Descuento > 0 ? `${articulo.Descuento}%` : '-'}
              </Text>
              <Text style={styles.colTotal}>{formatMoney(total)}</Text>
            </View>
          );
        })}
      </View>

      {/* Totales */}
      <View style={styles.totales}>
        <View style={styles.totalRow}>
          <Text>Subtotal:</Text>
          <Text>{formatMoney(cotizacion.subtotal)}</Text>
        </View>
        {cotizacion.descuentoTotal > 0 && (
          <View style={styles.totalRow}>
            <Text>Descuento Total:</Text>
            <Text>-{formatMoney(cotizacion.descuentoTotal)}</Text>
          </View>
        )}
        {cotizacion.impuestos.map((impuesto, index) => (
          <View key={index} style={styles.totalRow}>
            <Text>{impuesto.nombre}:</Text>
            <Text>{formatMoney(impuesto.valor)}</Text>
          </View>
        ))}
        <View style={[styles.totalRow, styles.bold]}>
          <Text>TOTAL:</Text>
          <Text>{formatMoney(cotizacion.totalAPagar)}</Text>
        </View>
      </View>

      {/* Observaciones */}
      {cotizacion.observaciones && (
        <View style={styles.observaciones}>
          <Text style={styles.subtitle}>OBSERVACIONES:</Text>
          <Text>{cotizacion.observaciones}</Text>
        </View>
      )}

      {/* Pie de Página */}
      <View style={styles.footer}>
        <Text>Esta cotización tiene una validez de 30 días a partir de su fecha de emisión.</Text>
        <Text style={styles.text}>Gracias por su interés en nuestros productos y servicios.</Text>
      </View>
    </Page>
  </Document>
);